import { DollarSign, CreditCard, CheckCircle, Clock, AlertCircle, ArrowRight } from 'lucide-react';
import { Badge } from './ui/badge';

/**
 * PaymentStatusBadge - Shows payment status with appropriate styling
 * For: Customer payment status and Provider payout status
 */
export function PaymentStatusBadge({ status, type = 'customer', amount, showAmount = true }) {
  const getStatusConfig = () => {
    if (type === 'customer') {
      switch (status) {
        case 'paid':
          return {
            label: 'Paid in Full',
            icon: CheckCircle,
            className: 'bg-green-100 text-green-800 border-green-300',
            iconColor: 'text-green-600'
          };
        case 'charging':
          return {
            label: 'Processing',
            icon: Clock,
            className: 'bg-yellow-100 text-yellow-800 border-yellow-300 animate-pulse',
            iconColor: 'text-yellow-600'
          };
        case 'pending':
        default:
          return {
            label: 'Payment Pending',
            icon: AlertCircle,
            className: 'bg-orange-100 text-orange-800 border-orange-300',
            iconColor: 'text-orange-600'
          };
      }
    } else {
      // Provider payout status
      switch (status) {
        case 'paid':
          return {
            label: 'Payment Sent',
            icon: CheckCircle,
            className: 'bg-green-100 text-green-800 border-green-300',
            iconColor: 'text-green-600'
          };
        case 'pending':
          return {
            label: 'Payment Pending',
            icon: Clock,
            className: 'bg-blue-100 text-blue-800 border-blue-300',
            iconColor: 'text-blue-600'
          };
        case 'awaiting':
        default:
          return {
            label: 'Awaiting Payment',
            icon: CreditCard,
            className: 'bg-gray-100 text-gray-800 border-gray-300',
            iconColor: 'text-gray-600'
          };
      }
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${config.className}`}>
      <Icon className={`w-4 h-4 ${config.iconColor}`} />
      <span className="text-sm font-medium">{config.label}</span>
      {showAmount && amount !== undefined && (
        <span className="font-bold">${typeof amount === 'number' ? amount.toFixed(2) : amount}</span>
      )}
    </div>
  );
}

/**
 * PaymentStatusCard - Full payment info card for job details pages
 */
export function PaymentStatusCard({ 
  customerPaymentStatus, 
  providerPaymentStatus,
  customerTotal,
  providerPay,
  showProviderInfo = true,
  userType = 'customer',
  autoChargeUrl = null
}) {
  return (
    <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border space-y-3">
      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-green-600" />
        Payment Status
      </h4>
      
      {/* Customer Payment */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">Customer Payment:</span>
        <PaymentStatusBadge 
          status={customerPaymentStatus} 
          type="customer" 
          amount={customerTotal}
        />
      </div>
      
      {/* Provider Payment - show to providers/admins */}
      {showProviderInfo && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">Provider Payout:</span>
          <PaymentStatusBadge 
            status={providerPaymentStatus} 
            type="provider" 
            amount={providerPay}
          />
        </div>
      )}
      
      {/* Auto-charge URL for pending payments */}
      {customerPaymentStatus === 'charging' && autoChargeUrl && userType === 'customer' && (
        <a 
          href={autoChargeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
        >
          <CreditCard className="w-4 h-4" />
          Complete Payment
          <ArrowRight className="w-4 h-4" />
        </a>
      )}
    </div>
  );
}

/**
 * PaymentStatusInline - Compact inline status for lists/tables
 */
export function PaymentStatusInline({ status, type = 'customer' }) {
  const getConfig = () => {
    if (type === 'customer') {
      switch (status) {
        case 'paid':
          return { label: 'Paid', color: 'bg-green-500' };
        case 'charging':
          return { label: 'Processing', color: 'bg-yellow-500 animate-pulse' };
        default:
          return { label: 'Pending', color: 'bg-orange-500' };
      }
    } else {
      switch (status) {
        case 'paid':
          return { label: 'Sent', color: 'bg-green-500' };
        case 'pending':
          return { label: 'Pending', color: 'bg-blue-500' };
        default:
          return { label: 'Awaiting', color: 'bg-gray-400' };
      }
    }
  };

  const config = getConfig();

  return (
    <Badge className={`${config.color} text-white text-xs`}>
      {config.label}
    </Badge>
  );
}

export default PaymentStatusBadge;
